import React, { useState, useMemo, useCallback } from "react";
import { Dropdown, Button, Space, Modal } from "antd";
import type { MenuProps } from "antd";
import { ApolloError } from "@apollo/client";

import MediaPlayer from "components/media-player/media-player";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { Properties, Maybe } from "graphql/generated/graphql";
import { useGetPropertyDocumentsQuery } from "graphql/queries/get-property-documents.generated";
import {
  AttachmentType,
  PropertyDocumentsType,
  MediaType,
} from "constants/field-values";

interface PropertyDocumentsModalProps {
  selectedProperty: Maybe<Properties> | undefined;
  onSelectProperty: (property: any) => void;
  isOpen: boolean;
  setIsOpen: (open: any) => void;
}

const PropertyDocumentsModal: React.FC<PropertyDocumentsModalProps> = (
  props: PropertyDocumentsModalProps
) => {
  const { selectedProperty, onSelectProperty, isOpen, setIsOpen } = props;
  const { loggedUserValue } = useLoggedUser();
  const [fileViewer, setFileViewer] = useState<{
    type: MediaType | null;
    src: string;
  }>({ type: null, src: "" });
  const [propertyDropdownOpen, setPropertyDropdownOpen] =
    useState<boolean>(false);

  const propertyOptions = useMemo(() => {
    const modalProperties: MenuProps["items"] = [];

    if (loggedUserValue.user_properties) {
      loggedUserValue.user_properties.forEach((element) => {
        const isSelected = selectedProperty?.id === element.property?.id;
        const containerClassName = isSelected
          ? "property-option-container selected"
          : "property-option-container";

        const newModalOption = {
          key: element.property?.id,
          icon: (
            <div className={containerClassName}>
              <span className="property-option-label">
                {element.property?.name}
              </span>
            </div>
          ),
        };

        modalProperties.push(newModalOption);
      });
    }

    return {
      modalProperties,
    };
  }, [loggedUserValue.user_properties, selectedProperty]);

  const handleOnOpenPDF = useCallback(async (document: any) => {
    if (document.document_link) {
      try {
        setFileViewer({
          type: MediaType.Document,
          src: document.document_link,
        });
      } catch (error) {
        console.log("handleOnOpenPDF error: ", error);
      }
    }
  }, []);

  const handleOnOpenVideo = useCallback((document: any) => {
    if (document.video_link) {
      setFileViewer({ type: MediaType.Video, src: document.video_link });
    }
  }, []);

  const handleOnErrorsGetPropertyDocuments = useCallback(
    (error: ApolloError) => {
      console.log("Get Property Documents Error: ", error);
    },
    []
  );

  const handleOnCompletedGetPropertyDocuments = useCallback((data) => {
    // console.log(
    //   "PropertyDocumentsModal Completed Get Property Documents: ",
    //   data
    // );
  }, []);

  const { data: propertyDocumentsData, loading } = useGetPropertyDocumentsQuery(
    {
      skip: !selectedProperty,
      variables: {
        id: selectedProperty?.id,
      },
      errorPolicy: "all",
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      onError: handleOnErrorsGetPropertyDocuments,
      onCompleted: handleOnCompletedGetPropertyDocuments,
    }
  );

  if (fileViewer.src && fileViewer.type !== null) {
    return (
      <MediaPlayer
        onCancel={() => setFileViewer({ type: null, src: "" })}
        src={fileViewer.src}
        open={true}
        mediaType={fileViewer.type}
      />
    );
  }

  return (
    <>
      <Modal
        loading={loading}
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        width={"95%"}
        footer={null}
        className="property-documents-modal"
      >
        <span className="property-documents-modal-title">
          Property Resources
        </span>

        <Dropdown
          className="property-dropdown"
          trigger={["click"]}
          menu={{
            items: propertyOptions.modalProperties,
            selectable: true,
            onSelect: (args) => onSelectProperty(args),
            onDeselect: (args) => onSelectProperty(args),
          }}
          onOpenChange={(open, info) => {
            setPropertyDropdownOpen((previousValue) => !previousValue);
          }}
          overlayClassName={"property-items-container"}
          open={propertyDropdownOpen}
        >
          <Button>
            <Space>
              {selectedProperty?.name
                ? selectedProperty.name
                : "Select Property"}
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/icons/black-chevron-down.svg"
                }
                title="Select Property"
                className="chevron-button"
              />
            </Space>
          </Button>
        </Dropdown>

        <div className="property-documents-container">
          {propertyDocumentsData?.property_documents.map((document, index) => {
            let imgSrc = `${process.env.PUBLIC_URL}/assets/images/icons/attachment-manual.svg`;
            let onClickAttachment;

            switch (document.type) {
              case AttachmentType.MANUAL:
              case AttachmentType.GENERAL_DOCUMENT:
              case AttachmentType.TECHNICAL_DOCUMENT:
              case PropertyDocumentsType.MARKUPS:
              case PropertyDocumentsType.OTHER:
              case "Relevant Document":
                imgSrc = `${process.env.PUBLIC_URL}/assets/images/icons/attachment-manual.svg`;
                onClickAttachment = () => handleOnOpenPDF(document);
                break;
              case AttachmentType.PHOTO:
              case AttachmentType.AUDIO:
              case AttachmentType.VIDEO:
              case "Tutorial Video":
                imgSrc = `${process.env.PUBLIC_URL}/assets/images/icons/attachment-video.svg`;
                onClickAttachment = () => handleOnOpenVideo(document);
                break;
              case AttachmentType.DIAGRAM:
              case PropertyDocumentsType.DESIGN:
                imgSrc = `${process.env.PUBLIC_URL}/assets/images/icons/attachment-doc.svg`;
                onClickAttachment = () => handleOnOpenPDF(document);
                break;
              default:
                break;
            }

            return (
              <div
                className="product-d-attachment"
                onClick={onClickAttachment}
                key={`product-d-attachment-${document.id}`}
              >
                <div className="icon-container">
                  <img
                    alt={document.type || `attachment-icon-${index}`}
                    src={imgSrc}
                  />
                </div>
                <span className="product-d-attachment-title">
                  {document.name || document.description}
                </span>
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
};

export default PropertyDocumentsModal;
